import { PickerResponse } from 'filestack-js/build/main/lib/picker';
import React, { ReactElement, useContext } from 'react';

import assetModalContext from '@components/asset/modal/tabs/edit/asset_modal_context';
import UppyUploader from '@components/common/uppy/main';
import { UploadArea } from '@components/common/filestack_uploader/UploadArea';
import { UploadAreaTypes } from '@components/common/filestack_uploader/UploadAreaEnums';
import {
  ActionTypes,
  WatermarkDispatch,
  WatermarkPayload,
  WatermarkPayloadOperations
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { WatermarkObject } from '@api/v4/assets/assetTypes';
import { UppyFile } from '@components/common/uppy/UppyTypes';
import { WatermarkPositions, defaultWatermarkPostion } from '@components/shared/watermarks';
import { WatermarkPositionsEnum } from '@components/shared/watermarks/WatermarkPositionTypes';

interface WatermarkScopedContext {
  state: {
    editState: {
      watermark: WatermarkObject;
    };
  };
  dispatch: WatermarkDispatch;
}

const WatermarkComponent = (): ReactElement => {
  const { state, dispatch }: WatermarkScopedContext = useContext(assetModalContext);
  const { watermark } = state.editState;

  const deleteAssetWatermark = (): void => {
    const payload = {
      operation: WatermarkPayloadOperations.Delete
    };

    dispatch({ type: ActionTypes.WatermarkAction, payload });
  };

  const updateWatermarkUppy = (files: UppyFile[]): void => {
    if (files.length === 0) return;

    const payload: WatermarkPayload = {
      ...watermark,
      gravity: defaultWatermarkPostion,
      filename: files[0].name,
      operation: WatermarkPayloadOperations.Update,
      url: files[0].url
    };

    dispatch({ type: ActionTypes.WatermarkAction, payload });

    let title = bfTranslate('Watermark will begin processing once changes are saved');

    if (BFG.hasFeature('watermarking')) {
      title = bfTranslate('Watermark will begin processing once changes are saved. Watermark will only be applied to supported file types: JPG, JPEG, and PNG.');
    }

    Notify.create({
      title,
      type: 'success',
    });
  };

  const updateWatermarkFilestack = (files: PickerResponse): void => {
    if (files.filesUploaded.length === 0) return;

    const payload: WatermarkPayload = {
      ...watermark,
      gravity: defaultWatermarkPostion,
      filename: files.filesUploaded[0].filename,
      operation: WatermarkPayloadOperations.Update,
      url: files.filesUploaded[0].url
    };

    dispatch({ type: ActionTypes.WatermarkAction, payload });

    let title = bfTranslate('Watermark will begin processing once changes are saved');

    if (BFG.hasFeature('watermarking')) {
      title = bfTranslate('Watermark will begin processing once changes are saved. Watermark will only be applied to supported file types: JPG, JPEG, and PNG.');
    }

    Notify.create({
      title,
      type: 'success',
    });
  };

  const onError = (): void => {
    Notify.create({ type: 'error', title: bfTranslate('Error Uploading Files') });
  };

  const handleWatermarkPosition = (watermarkPosition: WatermarkPositionsEnum): void => {
    const payload: WatermarkPayload = {
      ...watermark,
      gravity: watermarkPosition,
      operation: WatermarkPayloadOperations.Update
    };

    dispatch({ type: ActionTypes.WatermarkAction, payload });

    if (watermark.url) {
      let title = bfTranslate('Watermark position will reprocess once changes are saved');

      if (BFG.hasFeature('watermarking')) {
        title = bfTranslate('Watermark position will reprocess once changes are saved. Watermark will only be applied to supported file types: JPG, JPEG, and PNG.');
      }

      Notify.create({
        title,
        type: 'success',
      });
    }
  };

  const uploadAreaId = 'watermark-trigger-watermark';

  const uploadProvider = (): ReactElement => (
    BFG.hasFeature('uppy_uploader_features') ? (
      <>
        <div
          className="uppy-asset-watermark"
          id={uploadAreaId}
        >
          {bfTranslate('Click to upload')}
        </div>
        <UppyUploader
          button
          handleUpload={updateWatermarkUppy}
          restrictions={{ maxNumberOfFiles: 1, allowedFileTypes: ['image/*'] }}
          template="watermark"
          trigger={`#${uploadAreaId}`}
          uniqIdentifier="uppy-watermark-watermark"
        />
      </>
    ) : (
      <UploadArea
        id={uploadAreaId}
        onUploadDone={updateWatermarkFilestack}
        pickerOptions={{
          accept: ['image/png'],
          maxFiles: 1,
          onFileUploadFailed: onError
        }}
        uploadType={UploadAreaTypes.Watermark}
      />
    )
  );

  return (
    <>
      <div className="asset-advanced__feature">
        <span>{bfTranslate('Custom Watermark')}</span>
        <div className="watermark-upload">
          {
          (watermark.url && watermark.filename) ? (
            <div className="asset-advanced__feature--upload-complete">
              <a
                className="asset-advanced__feature--link filename"
                href={watermark.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {watermark.filename}
              </a>
              <span
                className="bff-trash remove-watermark"
                onClick={(): void => deleteAssetWatermark()}
                onKeyPress={(): void => deleteAssetWatermark()}
                role="button"
                tabIndex={0}
                title={bfTranslate('Remove Custom Asset Watermark')}
              />
            </div>
          ) : uploadProvider()
        }
        </div>
      </div>
      {watermark.url && BFG.hasFeature('watermarking') && (
        <div className="asset-advanced__feature watermark-positions__wrapper">
          <WatermarkPositions
            gravity={watermark.gravity || defaultWatermarkPostion}
            onChange={handleWatermarkPosition}
            showHeading={false}
          />
        </div>
      )}
    </>
  );
};

export default WatermarkComponent;
